/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/auth/RequireRole.tsx
 * PURPOSE: Role-based route guard HOC — blocks unauthorized role access
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-03-30
 * COMPLIANCE: SDLC 6.3.1 + Sprint 76 + Zero Mock Tolerance
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Frontend Authorization, Vietnamese F&B Platform
 * - Impact: P1 — CTO P2 #39, prevents role bypass via URL
 * - Stakeholders: All user roles, Security Team
 *
 * @technical-architecture
 * - Layer: UI Component (Authorization Guard)
 * - Pattern: HOC wrapping React Router Outlet/children
 * - Dependencies: React, react-router-dom, zustand (auth store)
 *
 * @cross-references
 * - Store: frontend/src/store/auth.ts (useAuthStore, userRole)
 * - Router: frontend/src/App.tsx (protected routes)
 * - Sprint: MRP-S76 Track 3
 */

import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../../store/auth";

interface RequireRoleProps {
  /** Allowed roles for this route (case-insensitive match) */
  allowedRoles: string[];
  /** Content to render when authorized */
  children: React.ReactNode;
  /** Redirect path for unauthorized users (default: role-appropriate dashboard) */
  redirectTo?: string;
}

/**
 * RequireRole — Route guard component.
 *
 * Usage in App.tsx:
 *   <Route path="/dashboard/manager/*" element={
 *     <RequireRole allowedRoles={["BRANCH_MANAGER", "DIRECTOR", "CHAIRMAN"]}>
 *       <ManagerDashboard />
 *     </RequireRole>
 *   } />
 *
 * Behavior:
 *   - Renders null until auth state is loaded (no flash of wrong content)
 *   - If user role matches allowedRoles → render children
 *   - If not → redirect to /dashboard/staff + Vietnamese toast
 */
export const RequireRole: React.FC<RequireRoleProps> = ({
  allowedRoles,
  children,
  redirectTo,
}) => {
  const { userRole, isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();
  const [showToast, setShowToast] = useState(false);

  // Normalize roles to uppercase for comparison
  const normalizedAllowed = allowedRoles.map((r) => r.toUpperCase());
  const normalizedUserRole = userRole?.toUpperCase() || "";

  // Wait for auth state to load — render nothing (no flash)
  if (isLoading) {
    return null;
  }

  // Not authenticated → redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Role check
  if (!normalizedAllowed.includes(normalizedUserRole)) {
    const target = redirectTo || "/dashboard/staff";

    return (
      <>
        <Navigate to={target} replace />
        <RoleToast />
      </>
    );
  }

  return <>{children}</>;
};

/**
 * Vietnamese toast notification for unauthorized access attempts.
 * "Bạn không có quyền truy cập trang này" — auto-hide after 5 seconds.
 */
const RoleToast: React.FC = () => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 16,
        right: 16,
        padding: "12px 20px",
        backgroundColor: "#dc2626",
        color: "#fff",
        borderRadius: 8,
        fontSize: 14,
        fontWeight: 500,
        zIndex: 9999,
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      }}
      role="alert"
    >
      Bạn không có quyền truy cập trang này
    </div>
  );
};

export default RequireRole;
